/**
 * The two sounds a payment screen needs.
 *
 * Synthesised with the Web Audio API rather than shipped as files: two short
 * tones weigh nothing, need no network, and cannot 404 on a shop's wifi. A till
 * that goes silent because an mp3 failed to load is a till whose staff stop
 * trusting the sound — and the whole value of an earcon is that it is reliable
 * enough to work without looking.
 *
 * The shapes are deliberate and conventional:
 *  · success — two rising notes. Resolution.
 *  · failure — one low note, flat. Not a klaxon: a decline is an ordinary event
 *    that happens several times a day, and an alarming noise in a quiet shop
 *    embarrasses the customer standing at the counter.
 *
 * Browsers refuse to make noise before the user has interacted with the page,
 * which is fine — every call site here happens after a tap.
 */
let context = null;

function audioContext() {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? window.webkitAudioContext;
  if (!Ctor) return null;

  // One context for the life of the page. Creating one per sound exhausts the
  // browser's limit after a few dozen sales.
  if (!context) context = new Ctor();
  if (context.state === 'suspended') context.resume().catch(() => {});
  return context;
}

/** A single sine note with a soft attack and decay, so nothing clicks. */
function note(ctx, { frequency, start, duration, gain = 0.09 }) {
  const osc = ctx.createOscillator();
  const envelope = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.value = frequency;

  const at = ctx.currentTime + start;
  envelope.gain.setValueAtTime(0.0001, at);
  envelope.gain.exponentialRampToValueAtTime(gain, at + 0.012);
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);

  osc.connect(envelope).connect(ctx.destination);
  osc.start(at);
  osc.stop(at + duration + 0.02);
}

/** Two rising notes — a fifth apart, which is why it reads as "done". */
export function playSuccess() {
  try {
    const ctx = audioContext();
    if (!ctx) return;
    note(ctx, { frequency: 660, start: 0, duration: 0.12 });
    note(ctx, { frequency: 990, start: 0.1, duration: 0.2 });
  } catch {
    /* audio is a courtesy; never let it break a payment screen */
  }
}

/** One low note. Informative, not alarming. */
export function playFailure() {
  try {
    const ctx = audioContext();
    if (!ctx) return;
    note(ctx, { frequency: 233, start: 0, duration: 0.26, gain: 0.075 });
  } catch {
    /* as above */
  }
}

/**
 * Picks the sound from an outcome, so call sites do not each decide what counts
 * as success. `review` and `unconfirmed` deliberately get the failure tone: the
 * money has not arrived, and a success chime would tell a cashier to hand over
 * the goods.
 */
export function playOutcome(result) {
  if (result === 'approved' || result === 'already_paid') playSuccess();
  else playFailure();
}

export default { playSuccess, playFailure, playOutcome };
